import type { ModelDefinition } from "@/models.js";

export const deepseekModels = [
	{
		id: "deepseek-v3",
		name: "DeepSeek V3",
		description:
			"Large-scale Chinese AI model with strong multilingual capabilities.",
		family: "deepseek",
		releasedAt: new Date("2024-12-26"),
		providers: [
			{
				providerId: "nebius",
				stability: "unstable" as const,
				externalId: "deepseek-ai/DeepSeek-V3",
				inputPrice: "0.5e-6",
				outputPrice: "1.5e-6",
				requestPrice: "0",
				contextSize: 64000,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: false,
				jsonOutput: false,
				deactivatedAt: new Date("2025-11-03"),
			},
		],
	},
	{
		id: "deepseek-r1-0528",
		name: "DeepSeek R1 (0528)",
		description: "May 2028 version of DeepSeek R1 reasoning model.",
		family: "deepseek",
		releasedAt: new Date("2025-05-28"),
		providers: [
			{
				providerId: "nebius",
				externalId: "deepseek-ai/DeepSeek-R1-0528",
				inputPrice: "0.8e-6",
				outputPrice: "2.4e-6",
				requestPrice: "0",
				contextSize: 64000,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: false,
				stability: "unstable" as const,
				jsonOutput: false,
				deactivatedAt: new Date("2026-04-25"),
			},
		],
	},
	{
		id: "deepseek-r1-distill-llama-70b",
		name: "DeepSeek R1 Distill Llama 70B",
		description: "DeepSeek R1 distilled into Llama 70B architecture.",
		family: "deepseek",
		stability: "beta" as const,
		releasedAt: new Date("2025-01-20"),
		providers: [
			{
				providerId: "groq",
				externalId: "deepseek-r1-distill-llama-70b",
				inputPrice: "0.75e-6",
				outputPrice: "0.99e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2025-10-09"),
			},
		],
	},
	{
		id: "deepseek-v3.1",
		name: "DeepSeek V3.1",
		description: "Updated DeepSeek V3 with tools and improved performance.",
		family: "deepseek",
		releasedAt: new Date("2025-08-21"),
		providers: [
			{
				providerId: "bytedance",
				externalId: "deepseek-v3-1-250821",
				inputPrice: "0.56e-6",
				cachedInputPrice: "0.112e-6",
				outputPrice: "1.68e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 32768,
				reasoning: true,
				reasoningOutput: "omit" as const,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: false,
			},
		],
	},
	{
		id: "deepseek-v3.2",
		name: "DeepSeek V3.2",
		description: "Latest DeepSeek V3 with tools and improved performance.",
		family: "deepseek",
		releasedAt: new Date("2025-09-29"),
		providers: [
			{
				providerId: "deepseek",
				externalId: "deepseek-chat",
				inputPrice: "0.28e-6",
				outputPrice: "0.42e-6",
				cachedInputPrice: "0.028e-6",
				requestPrice: "0",
				contextSize: 163840,
				maxOutput: undefined,
				jsonOutput: true,
				streaming: true,
				vision: false,
				tools: true,
				deactivatedAt: new Date("2026-05-01"),
			},
			{
				providerId: "novita",
				externalId: "deepseek/deepseek-v3.2",
				inputPrice: "0.269e-6",
				cachedInputPrice: "0.1345e-6",
				outputPrice: "0.4e-6",
				requestPrice: "0",
				contextSize: 163840,
				maxOutput: 65536,
				reasoning: true,
				requiresEnableThinking: true,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "alibaba",
				externalId: "deepseek-v3.2",
				inputPrice: "0.57e-6",
				cachedInputPrice: "0.114e-6",
				outputPrice: "1.71e-6",
				regions: [
					{ id: "singapore" },
					{
						id: "cn-beijing",
						inputPrice: "0.287e-6",
						cachedInputPrice: "0.0574e-6",
						outputPrice: "0.431e-6",
					},
				],
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 65536,
				reasoning: false,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-07-08"),
			},
			{
				providerId: "bytedance",
				externalId: "deepseek-v3-2-251201",
				inputPrice: "0.28e-6",
				cachedInputPrice: "0.056e-6",
				outputPrice: "0.42e-6",
				requestPrice: "0",
				contextSize: 131072,
				reasoning: true,
				reasoningOutput: "omit" as const,
				maxOutput: 131072,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: false,
			},
			{
				providerId: "nebius",
				externalId: "deepseek-ai/DeepSeek-V3.2",
				deactivatedAt: new Date("2026-06-22"),
				inputPrice: "0.3e-6",
				outputPrice: "0.45e-6",
				requestPrice: "0",
				contextSize: 163840,
				maxOutput: 32768,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "deepinfra",
				externalId: "deepseek-ai/DeepSeek-V3.2",
				inputPrice: "0.26e-6",
				cachedInputPrice: "0.13e-6",
				outputPrice: "0.38e-6",
				requestPrice: "0",
				contextSize: 160000,
				maxOutput: 65536,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "deepseek-v4-pro",
		name: "DeepSeek V4 Pro",
		description:
			"DeepSeek's most capable V4 model with extended context and reasoning.",
		family: "deepseek",
		releasedAt: new Date("2026-04-24"),
		providers: [
			{
				providerId: "deepseek",
				externalId: "deepseek-v4-pro",
				inputPrice: "0.435e-6",
				outputPrice: "0.87e-6",
				cachedInputPrice: "0.003625e-6",
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 393216,
				jsonOutput: true,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
				],
			},
			{
				providerId: "together-ai",
				externalId: "deepseek-ai/DeepSeek-V4-Pro",
				inputPrice: "1.74e-6",
				cachedInputPrice: "0.2e-6",
				outputPrice: "3.48e-6",
				requestPrice: "0",
				contextSize: 163840,
				maxOutput: 163840,
				streaming: true,
				reasoning: true,
				reasoningOutput: "omit",
				vision: false,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "alibaba",
				externalId: "deepseek-v4-pro",
				inputPrice: "2.4e-6",
				cachedInputPrice: "0.2e-6",
				outputPrice: "4.8e-6",
				regions: [
					{ id: "singapore" },
					{
						id: "cn-beijing",
						inputPrice: "1.65e-6",
						cachedInputPrice: "0.138e-6",
						outputPrice: "3.301e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 393216,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "deepinfra",
				externalId: "deepseek-ai/DeepSeek-V4-Pro",
				inputPrice: "1.74e-6",
				cachedInputPrice: "0.145e-6",
				outputPrice: "3.48e-6",
				requestPrice: "0",
				contextSize: 64000,
				maxOutput: 64000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "deepseek-v4-flash",
		name: "DeepSeek V4 Flash",
		description:
			"DeepSeek's fast and cost-efficient V4 model with extended context and reasoning.",
		family: "deepseek",
		releasedAt: new Date("2026-04-24"),
		providers: [
			{
				providerId: "deepseek",
				externalId: "deepseek-v4-flash",
				inputPrice: "0.14e-6",
				outputPrice: "0.28e-6",
				cachedInputPrice: "0.0028e-6",
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 393216,
				jsonOutput: true,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
				],
			},
			{
				providerId: "novita",
				externalId: "deepseek/deepseek-v4-flash",
				inputPrice: "0.14e-6",
				cachedInputPrice: "0.028e-6",
				outputPrice: "0.28e-6",
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 393216,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "alibaba",
				externalId: "deepseek-v4-flash",
				inputPrice: "0.2e-6",
				cachedInputPrice: "0.04e-6",
				outputPrice: "0.4e-6",
				regions: [
					{ id: "singapore" },
					{
						id: "cn-beijing",
						inputPrice: "0.138e-6",
						cachedInputPrice: "0.028e-6",
						outputPrice: "0.275e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 393216,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "deepinfra",
				externalId: "deepseek-ai/DeepSeek-V4-Flash",
				inputPrice: "0.14e-6",
				cachedInputPrice: "0.028e-6",
				outputPrice: "0.28e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 393216,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
] as const satisfies ModelDefinition[];
